/**
 * Hiding always-on-top host chrome while this module owns the screen.
 *
 * 🔴 ONE IMPLEMENTATION FOR EVERY SURFACE THAT NEEDS IT.
 * The sandbox badge is fixed to the bottom-left at `z-index: 2147483647` — the
 * maximum a stacking context can hold — so NOTHING can be layered above it. It has
 * already covered a highlighted sidebar card and made the fallback launcher
 * unclickable; on a short viewport it lands on the demo dialog's footer buttons
 * too. A second copy of this logic in the dialog would drift from the engine's the
 * first time the selector list changed, so both call these.
 *
 * Attribute-driven rather than inline styles, so the visual lives in the module's
 * stylesheet with everything else, and reversible so nothing is ever left hidden.
 */

import { HOST } from './anchors';

/** Marks host chrome hidden for as long as this module needs the space. */
export const SUPPRESSED_ATTR = 'data-tour-suppressed';

/**
 * Hide the configured chrome and return exactly the elements touched, so teardown
 * restores those and not whatever happens to match later.
 */
export function suppressHostChrome(): HTMLElement[] {
  const hidden: HTMLElement[] = [];
  for (const selector of HOST.suppressDuringTour) {
    try {
      document.querySelectorAll(selector).forEach((el) => {
        el.setAttribute(SUPPRESSED_ATTR, '');
        hidden.push(el as HTMLElement);
      });
    } catch {
      // A bad selector must not stop the tour from starting, or a dialog from
      // opening. Nothing matched means nothing to hide — which is also the
      // correct outcome in a host that has no such chrome.
    }
  }
  return hidden;
}

export function restoreHostChrome(elements: HTMLElement[]): void {
  elements.forEach((el) => el.removeAttribute(SUPPRESSED_ATTR));
}
